// Mic acquisition + live volume analysis for speaking tasks.
//
// The previous flow was: getUserMedia -> MediaRecorder -> submit. Two silent
// failure modes: (1) Safari sometimes returns a stream whose track is muted
// (permission-not-granted-but-not-denied), so the recorder ran and produced an
// almost-empty webm; (2) an unplugged mic returns a live-track stream that
// records nothing. Both paths sent zero-audio blobs to the backend which then
// hallucinated a CLB score against silence.
//
// This module wraps the pipeline with three real checks:
//   1. After getUserMedia, verify at least one audio track is live + unmuted.
//   2. Attach an AnalyserNode during recording to compute a live RMS level.
//   3. Track whether the RMS ever crossed a "someone spoke" threshold. If not,
//      the stop callback returns `clientSilent: true` so the frontend can
//      refuse to submit and the backend can gate-zero if it does.
//
// No feature flags, no fallbacks; a broken stream stops the exam BEFORE the
// grader is called.

const SPEECH_RMS_THRESHOLD = 0.035;
const MIME_CANDIDATES = [
  { mime: 'audio/webm;codecs=opus', ext: 'webm' },
  { mime: 'audio/webm', ext: 'webm' },
  { mime: 'audio/mp4', ext: 'm4a' },
  { mime: 'audio/aac', ext: 'aac' },
];

function pickMime() {
  const MR = typeof window !== 'undefined' ? window.MediaRecorder : null;
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const c of MIME_CANDIDATES) if (MR.isTypeSupported(c.mime)) return c;
  }
  return { mime: '', ext: 'webm' };
}

// Human-readable error text for the intro card. Uses the browser-provided
// error name so the copy matches the exact failure the student hit.
function micErrorFor(err) {
  const name = (err && err.name) || '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone is blocked. Open the site permissions in your browser (the lock icon in the address bar on Safari and Chrome), allow the microphone, then click Start again.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') {
    return 'No microphone was found. Plug in or enable a mic, then click Start again.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'The microphone is in use by another app. Close FaceTime, Zoom, or any other app using the mic, then click Start again.';
  }
  return 'The browser could not open the microphone. Reload the page and click Start again.';
}

// Wait up to `timeoutMs` for a MediaStreamTrack that reports `muted === true`
// to fire its `unmute` event. Chromium and Safari both ship a stream from
// getUserMedia() whose track is transiently muted (the source hasn't produced
// samples yet); the flag flips within a few hundred ms once the mic driver
// starts pumping. Previously we rejected on that transient state and showed
// the student a fake "microphone is blocked" screen on item 2/3+ even though
// the browser permission was fine — no fresh prompt appears on repeat calls
// because the origin already has the grant.
function waitForUnmute(track, timeoutMs = 1500) {
  if (!track) return Promise.resolve(false);
  if (!track.muted) return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    const finish = (ok) => { if (done) return; done = true; cleanup(); resolve(ok); };
    const onUnmute = () => finish(true);
    const timer = setTimeout(() => finish(!track.muted), timeoutMs);
    const cleanup = () => {
      clearTimeout(timer);
      try { track.removeEventListener('unmute', onUnmute); } catch { /* ignore */ }
    };
    try { track.addEventListener('unmute', onUnmute); } catch { /* ignore */ }
  });
}

// Acquire the mic stream. Rejects with a user-facing message if permission is
// blocked OR if the returned stream has no live audio track.
export async function acquireMicStream() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error('This browser does not support recording. Use a recent Chrome, Edge, Firefox, or Safari.');
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch((err) => {
    const e = new Error(micErrorFor(err));
    e.cause = err;
    throw e;
  });
  const tracks = stream.getAudioTracks();
  let live = tracks.find((t) => t.readyState === 'live' && !t.muted && t.enabled);
  if (!live) {
    // Give a transiently-muted track a chance to unmute before we call the
    // mic dead. The RMS level check during recording is the real gate for
    // silent submissions, so a short wait here costs nothing.
    const candidate = tracks.find((t) => t.readyState === 'live' && t.enabled);
    if (candidate) {
      const ok = await waitForUnmute(candidate, 1500);
      if (ok && !candidate.muted) live = candidate;
    }
  }
  if (!live) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error('The microphone opened but is not sending audio. Check the OS mic permission for this browser (Mac: System Settings > Privacy & Security > Microphone), then click Start again.');
  }
  return { stream, mime: pickMime() };
}

// Build a live volume meter + speech-detection loop for an active stream.
// Returns:
//   getLevel()      -> 0..1 RMS, poll from a React ref/animation frame
//   didSpeak()      -> true if RMS ever crossed threshold during the loop
//   quietMs()       -> ms since last speech peak (used by ASQ auto-stop)
//   stop()          -> tear down the AudioContext (safe to call twice)
export function buildLevelMeter(stream) {
  let ctx;
  let raf = 0;
  let level = 0;
  let spoke = false;
  let lastPeakAt = 0;
  let startedAt = Date.now();
  try {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const tick = () => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      level = rms;
      if (rms > SPEECH_RMS_THRESHOLD) {
        spoke = true;
        lastPeakAt = Date.now();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  } catch {
    // Analyser is best-effort; if it fails the recorder still runs and the
    // backend's byte/duration floor still catches silent submissions.
  }
  return {
    getLevel: () => level,
    didSpeak: () => spoke,
    quietMs: () => (lastPeakAt ? Date.now() - lastPeakAt : Date.now() - startedAt),
    stop: () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      if (ctx) { ctx.close().catch(() => {}); ctx = null; }
    },
  };
}

export function releaseStream(stream) {
  if (stream) stream.getTracks().forEach((t) => t.stop());
}
